import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import { Button, Paper, Tooltip, Typography } from '@mui/material';
import { Box, Stack, SxProps, Theme } from '@mui/system';

import WaveUICheckbox from '@/shared/components/forms/WaveUICheckbox';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import WaveUINoDataOverlay from '@/shared/components/utilities/WaveUINoDataOverlay';

import useAssignStates from './hooks/useAssignStates';

const stylesConfig: Record<string, SxProps<Theme>> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(200px, 1fr) auto minmax(20px, 1fr)',
    gap: '8px 16px',
  },
  panel: {
    px: 2,
    pb: 2,
    pt: 0,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 300,
    overflow: 'auto',
    height: '100%',
  },
  panelError: {
    border: 1,
    borderColor: 'error.main',
  },
  actionIcon: {
    color: 'text.secondary',
    width: 16,
    height: 16,
  },
  searchPanel: {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 1,
    pt: 2,
  },
};

type AssignStatesProps = {
  assignStatesState: ReturnType<typeof useAssignStates>;
  validationError?: string;
};

const AssignStates: FC<AssignStatesProps> = ({
  assignStatesState,
  validationError,
}) => {
  const { t } = useTranslation(['shipping', 'common']);

  const {
    available,
    assigned,
    search,
    onSearch,
    assignAll,
    unassignAll,
    assignSelected,
    unassignSelected,
    selectAvailableItem,
    selectAssignedItem,
  } = assignStatesState;

  return (
    <Stack sx={stylesConfig.grid}>
      <Stack sx={{ ...stylesConfig.grid, gridColumn: '1/-1' }}>
        <Typography variant='caption' color='text.secondary' textAlign='center'>
          {t('shipping:available')}
        </Typography>
        <div />
        <Typography variant='caption' color='text.secondary' textAlign='center'>
          {t('shipping:assigned')}
        </Typography>
      </Stack>

      <Paper sx={stylesConfig.panel}>
        <Box sx={stylesConfig.searchPanel}>
          <WaveUIForm_V2
            fields={[
              {
                field_type: FieldType.Text,
                field_props: {
                  name: 'search',
                  label: '',
                  placeholder: 'Search...',
                  value: search.available,
                  startAdornment: (
                    <SearchIcon
                      sx={{
                        width: 20,
                        height: 20,
                        color: 'text.secondary',
                      }}
                    />
                  ),
                  inputProps: {
                    sx: {
                      p: 1.2,
                    },
                  },
                },
              },
            ]}
            getFieldsValues={(value) => {
              const { search } = value;
              onSearch(search as string, 'available');
            }}
          />
        </Box>

        {available.length === 0 && (
          <WaveUINoDataOverlay title={t('shipping:noAvailable')} size='small' />
        )}
        {available
          .filter((item) => item.visible)
          .map((item) => (
            <WaveUICheckbox
              key={item.value}
              name={item.label}
              label={item.label}
              checked={item.checked}
              onChange={(e) =>
                selectAvailableItem(e.target.name, e.target.checked)
              }
            />
          ))}
      </Paper>

      <Stack spacing={1}>
        <Button variant='outlined' sx={{ p: 2 }} onClick={assignAll}>
          <KeyboardDoubleArrowRightIcon sx={stylesConfig.actionIcon} />
        </Button>
        <Button variant='outlined' sx={{ p: 2 }} onClick={assignSelected}>
          <KeyboardArrowRightIcon sx={stylesConfig.actionIcon} />
        </Button>
        <Button variant='outlined' sx={{ p: 2 }} onClick={unassignSelected}>
          <KeyboardArrowLeftIcon sx={stylesConfig.actionIcon} />
        </Button>
        <Button variant='outlined' sx={{ p: 2 }} onClick={unassignAll}>
          <KeyboardDoubleArrowLeftIcon sx={stylesConfig.actionIcon} />
        </Button>
      </Stack>

      <Stack spacing={1}>
        <Paper
          sx={() => ({
            ...stylesConfig.panel,
            ...(validationError ? stylesConfig.panelError : {}),
          })}
        >
          <Box sx={stylesConfig.searchPanel}>
            <WaveUIForm_V2
              fields={[
                {
                  field_type: FieldType.Text,
                  field_props: {
                    name: 'search',
                    label: '',
                    placeholder: 'Search...',
                    value: search.assigned,
                    startAdornment: (
                      <SearchIcon
                        sx={{
                          width: 20,
                          height: 20,
                          color: 'text.secondary',
                        }}
                      />
                    ),
                    inputProps: {
                      sx: {
                        p: 1.2,
                      },
                    },
                  },
                },
              ]}
              getFieldsValues={(value) => {
                const { search } = value;
                onSearch(search as string, 'assigned');
              }}
            />
          </Box>
          {assigned.length === 0 && (
            <WaveUINoDataOverlay
              title={t('shipping:noAssigned')}
              size='small'
            />
          )}
          {assigned
            .filter((item) => item.visible)
            .map((item) => (
              <Stack
                key={item.value}
                direction='row'
                alignItems='center'
                spacing={1}
                useFlexGap
              >
                <WaveUICheckbox
                  name={item.label}
                  label={item.label}
                  checked={item.checked}
                  onChange={(e) =>
                    selectAssignedItem(e.target.name, e.target.checked)
                  }
                />
                {item?.error && (
                  <Tooltip title={item.error} placement='top' arrow>
                    <InfoOutlinedIcon width={16} height={16} color='error' />
                  </Tooltip>
                )}
              </Stack>
            ))}
        </Paper>
        {!!validationError && (
          <Typography variant='body2' color='error'>
            {validationError}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default AssignStates;
